import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges, ViewChild } from '@angular/core';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { AuthService } from '@shared/services/auth.service';
import { CountryService } from '@shared/services/country.service';
import {
  ActionEnum,
  Actions,
  Causes,
  CREDIBILITY_FACTOR,
  LogFiltersType,
  LogsService,
} from '@shared/services/logs/logs.service';
import { TooltipMenuComponent } from '@shared/components/tooltip-menu/tooltip-menu.component';
import { copyObject, defined, sortAlphabetically } from '@shared/utils/data-utils';
import { ToastrService } from 'ngx-toastr';
import {
  isValidHttpCode,
  isValidHttpMethod,
  isValidIpOrCidr,
  isValidPositiveInt,
  validatePositiveInt0to100,
} from '@shared/validators/validators';
import { firstValueFrom } from 'rxjs';
import { map } from 'rxjs/operators';
import { Filter, FilterDefinition, FilterDefinitions, Filters, FiltersEnum, FilterType, initData } from './filters';
import { FeatureFlagService } from '@shared/services/feature-flag.service';
import _ from 'lodash';
import { cacheStatusOrder } from '@shared/components/highcharts/graph/graph';
import { KeyValuePipe, NgClass, NgFor, NgIf, NgStyle, NgSwitch, NgSwitchCase } from '@angular/common';
import { TooltipMenuComponent as TooltipMenuComponent_1 } from '../../../shared/components/tooltip-menu/tooltip-menu.component';
import { NgLabelTemplateDirective, NgOptionComponent, NgSelectComponent } from '@ng-select/ng-select';
import { FormsModule } from '@angular/forms';
import { TranslateLabelPipe } from '@shared/pipe/filter.pipe';
import { countryCodeToEmoji } from '@shared/utils/flags';

export const FilterableActions = Object.values(ActionEnum).filter((a) => a != ActionEnum.REPLIED);

interface Choice {
  slug: string;
  value: string | number | boolean;
}

enum Mode {
  ADD = 'add',
  EDIT = 'edit',
}
export const UAChoices = [
  { slug: 'Chrome', value: 'Chrome' },
  { slug: 'Edge', value: 'Edge' },
  { slug: 'Firefox', value: 'Firefox' },
  { slug: 'Chrome Mobile', value: 'Chrome Mobile' },
  { slug: 'Safari', value: 'Safari' },
  { slug: 'Mobile Safari', value: 'Mobile Safari' },
  { slug: 'Outlook-iOS-Android', value: 'Outlook-iOS-Android' },
  { slug: 'Chrome Mobile iOS', value: 'Chrome Mobile iOS' },
  { slug: 'Electron', value: 'Electron' },
  { slug: 'Samsung Internet', value: 'Samsung Internet' },
  { slug: 'Meta', value: 'crawler' },
  { slug: 'Mobile Safari UI/WKWebView', value: 'Mobile Safari UI/WKWebView' },
  { slug: 'Chrome mobile WebView', value: 'Chrome mobile WebView' },
  { slug: 'ExchangeWebServices', value: 'ExchangeWebServices' },
  { slug: 'HeadlessChrome', value: 'HeadlessChrome' },
  { slug: 'Android', value: 'Android' },
  { slug: 'Amazon Product Discovery', value: 'amazonproductbot' },
];

@Component({
  selector: 'app-filters',
  templateUrl: './filters.component.html',
  styleUrls: ['./filters.component.scss'],
  imports: [
    NgClass,
    NgFor,
    NgStyle,
    NgIf,
    NgSwitch,
    NgSwitchCase,
    TooltipMenuComponent_1,
    NgSelectComponent,
    FormsModule,
    NgOptionComponent,
    NgLabelTemplateDirective,
    KeyValuePipe,
    TranslatePipe,
    TranslateLabelPipe,
  ],
})
export class FiltersComponent implements OnInit, OnChanges {
  @Input() isLoading: boolean = false;
  @Input() filtersInput?: Filter[] = [];

  @Output() filtersEmitter = new EventEmitter<Filter[]>();

  @ViewChild('tooltipNewFilter')
  tooltipNewFilter: TooltipMenuComponent;

  @ViewChild('tooltipFilterMenu')
  tooltipFilterMenu: TooltipMenuComponent;

  lang: string;

  Mode = Mode;
  mode: Mode;
  currentFilterElementRef: HTMLElement = null;
  currentFilter: Filter = null;
  selectedFilter: FiltersEnum;

  FilterType = FilterType;
  LogFiltersType = LogFiltersType;
  FiltersEnum = FiltersEnum;

  filters: Filters = new Map<FiltersEnum, Filter>();

  filterDefinitions: FilterDefinition[] = [
    {
      key: FiltersEnum.IP,
      slug: 'IPs',
      type: FilterType.MULTI_IN_NOTIN,
      tooltip: 'TooltipIPsMyLogs',
      canAddChoice: true,
    },
    {
      key: FiltersEnum.USER_AGENT,
      slug: 'User Agent',
      type: FilterType.MULTI_IN_NOTIN,
      choices: UAChoices,
      canAddChoice: true,
    },
    {
      key: FiltersEnum.STATUS_CODE,
      slug: 'StatusCodes',
      type: FilterType.MULTI_IN_NOTIN,
      tooltip: 'StatusCodesTooltip',
      canAddChoice: true,
      choices: [
        { slug: '2XX', value: '2XX' },
        { slug: '200', value: '200' },
        { slug: '201', value: '201' },
        { slug: '204', value: '204' },
        { slug: '206', value: '206' },
        { slug: '3XX', value: '3XX' },
        { slug: '301', value: '301' },
        { slug: '302', value: '302' },
        { slug: '304', value: '304' },
        { slug: '4XX', value: '4XX' },
        { slug: '400', value: '400' },
        { slug: '401', value: '401' },
        { slug: '403', value: '403' },
        { slug: '404', value: '404' },
        { slug: '405', value: '405' },
        { slug: '499', value: '499' },
        { slug: '5XX', value: '5XX' },
        { slug: '500', value: '500' },
        { slug: '502', value: '502' },
        { slug: '503', value: '503' },
        { slug: '504', value: '504' },
      ],
    },
    {
      key: FiltersEnum.ACTION,
      slug: 'Action.label',
      type: FilterType.MULTI_IN_NOTIN,
      tooltip: 'ActionTooltip',
      choices: Actions.filter((a) => a != ActionEnum.REPLIED).map((action) => ({
        slug: this.translate.instant(`Action.${action}`),
        value: action,
      })),
      visibleChoices: FilterableActions.map((action) => ({
        slug: this.translate.instant(`Action.${action}`),
        value: action,
      })),
    },
    {
      key: FiltersEnum.CAUSE,
      slug: 'Cause.label',
      type: FilterType.MULTI_IN_NOTIN,
      tooltip: 'CauseTooltip',
      choices: Causes.map((cause) => ({
        slug: this.translate.instant(`Cause.${cause}`),
        value: cause,
      })),
    },
    {
      key: FiltersEnum.COUNTRY,
      slug: 'Pays',
      type: FilterType.MULTI_IN_NOTIN,
      choices: [],
    },
    {
      key: FiltersEnum.PATH,
      slug: 'ExactPath',
      type: FilterType.MULTI_IN_NOTIN,
      canAddChoice: true,
    },
    {
      key: FiltersEnum.PATH_STARTS_WITH,
      slug: 'PathStartsWith',
      type: FilterType.MULTI_IN_NOTIN,
      canAddChoice: true,
    },
    {
      key: FiltersEnum.RESPONSE_TIME_GTE,
      slug: 'ResponseTimeGte',
      type: FilterType.SINGLE_INPUT,
      suffix: 'ms',
    },
    {
      key: FiltersEnum.CONTENT_LENGTH_GTE,
      slug: 'ContentLengthGte',
      type: FilterType.SINGLE_INPUT,
      suffix: 'bytes',
    },
    {
      key: FiltersEnum.CACHE_STATUS,
      slug: 'CacheStatus.FilterLabel',
      type: FilterType.MULTI_IN_NOTIN,
      tooltip: 'CacheTooltip',
      choices: cacheStatusOrder.map((cacheStatus) => ({
        slug: this.translate.instant(`CacheStatus.${cacheStatus}`),
        value: cacheStatus,
      })),
    },
    {
      key: FiltersEnum.METHOD,
      slug: 'Method',
      type: FilterType.MULTI_IN_NOTIN,
      choices: [
        { slug: 'GET', value: 'GET' },
        { slug: 'POST', value: 'POST' },
        { slug: 'PUT', value: 'PUT' },
        { slug: 'DELETE', value: 'DELETE' },
      ],
      canAddChoice: true,
    },
    {
      key: FiltersEnum.ATTACK_CATEGORY,
      slug: 'TypesAnomalies',
      type: FilterType.MULTI_IN_NOTIN,
      choices: [],
      canAddChoice: false,
    },
    {
      key: FiltersEnum.CREDIBILITY_BETWEEN,
      slug: 'CredibilityBetween',
      type: FilterType.INTERVAL,
      canAddChoice: true,
      suffix: '%',
      factor: CREDIBILITY_FACTOR,
    },
    {
      key: FiltersEnum.BOT_UA,
      slug: 'BotUA',
      type: FilterType.SINGLE_SELECT,
      choices: [
        { slug: this.translate.instant('Yes'), value: true },
        { slug: this.translate.instant('No'), value: false },
      ],
      canAddChoice: false,
    },
  ];
  private pendingChoices = new Map<FiltersEnum, Promise<void>>();

  allFilterDefinitions!: FilterDefinitions;
  availableFilterDefinitions!: FilterDefinition[];

  get canAddEditFilter(): boolean {
    switch (this.currentFilter?.filterDefinition?.type) {
      case FilterType.SINGLE_INPUT:
        return !!(this.currentFilter?.data as string)?.length;

      case FilterType.SINGLE_SELECT:
        return defined(this.currentFilter?.data as boolean);

      case FilterType.INTERVAL:
        return (
          defined(this.currentFilter?.data[LogFiltersType.GTE]) && defined(this.currentFilter?.data[LogFiltersType.LTE])
        );

      case FilterType.MULTI_IN_NOTIN:
        return (
          !!this.currentFilter?.data[LogFiltersType.IN].length ||
          !!this.currentFilter?.data[LogFiltersType.NOTIN].length
        );

      default:
        return false;
    }
  }

  get hasFilterActive(): boolean {
    return Array.from(this.filters.values()).some((f) => f.active);
  }

  constructor(
    public translate: TranslateService,
    private auth: AuthService,
    private toastr: ToastrService,
    private countryService: CountryService,
    private logsService: LogsService,
    private featureFlagService: FeatureFlagService,
  ) {}

  ngOnInit(): void {
    this.lang = this.auth.getCurrentLanguage();
    this.initFilterDefinitions();
    this.getCountries();
    this.getAttacksCategories();

    this.initFiltersFromInput();
  }

  initFilterDefinitions() {
    const filtered = this.filterDefinitions.filter(
      (f) => !f.feature || this.featureFlagService.featureEnabled(f.feature),
    );
    filtered.find((f) => f.key === FiltersEnum.USER_AGENT)?.choices.sort((a, b) => a.slug.localeCompare(b.slug));
    this.allFilterDefinitions = _.keyBy(filtered, (f) => f.key) as FilterDefinitions;
    this.availableFilterDefinitions = Object.values(this.allFilterDefinitions);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['filtersInput']) {
      this.initFiltersFromInput();
    }
  }

  async getCountries(): Promise<void> {
    await this.loadChoices(FiltersEnum.COUNTRY, async () => {
      await this.countryService.init();

      return this.countryService.getCountries().map((c) => ({
        slug: `${countryCodeToEmoji(c.code)} ${c.name}`,
        value: c.code,
      }));
    });
  }

  customSearchFn = (term: string, item: any): boolean => {
    const search = term.toLowerCase();
    return (
      item.slug?.toLowerCase().includes(search) ||
      String(item.value ?? '')
        .toLowerCase()
        .includes(search)
    );
  };
  async getAttacksCategories(): Promise<void> {
    await this.loadChoices(FiltersEnum.ATTACK_CATEGORY, () =>
      firstValueFrom(
        this.logsService.getAttacksCategories().pipe(
          map((categories) =>
            categories
              .filter((c) => !c.hidden)
              .map((category) => ({
                slug: category.name,
                value: category.uid,
              })),
          ),
        ),
      ),
    );
  }

  async createFilter(
    type: LogFiltersType | null,
    key: FiltersEnum,
    value: string | number,
    label?: string,
  ): Promise<boolean> {
    if (key == FiltersEnum.COUNTRY) {
      await this.getCountries();
    } else if (key == FiltersEnum.ATTACK_CATEGORY) {
      await this.getAttacksCategories();
    }

    const filterDefinition = this.allFilterDefinitions[key];

    let reference = this.filters.get(key) || this.initFilter(filterDefinition);

    const dataToAdd = {
      slug: label || filterDefinition.choices?.find((c) => c.value == value)?.slug || value,
      value,
    };

    if (reference) {
      if (filterDefinition.type == FilterType.MULTI_IN_NOTIN) {
        if (reference.data[type].find((d) => d.value == dataToAdd.value)) {
          return false;
        }

        if (type == LogFiltersType.NOTIN) {
          const toTransfer = reference.data[LogFiltersType.IN].findIndex((d) => d.value == dataToAdd.value);

          if (toTransfer != -1) {
            reference.data[LogFiltersType.IN].splice(toTransfer, 1);
          }
        }

        reference.data[type].push(dataToAdd);
      } else if (filterDefinition.type == FilterType.SINGLE_INPUT) {
        reference.data = value;
      }

      this.filters.set(key, reference);

      return true;
    }

    return false;
  }

  onSelectedFilterChange(): void {
    this.currentFilter = this.initFilter(this.allFilterDefinitions[this.selectedFilter]);
  }

  initFilter(filterDefinition: FilterDefinition): Filter {
    return {
      active: true,
      data: _.cloneDeep(initData[filterDefinition.type]),
      filterDefinition,
    };
  }

  openEditFilterMenu(event, filter: Filter): void {
    this.currentFilter = copyObject(filter);
    this.currentFilterElementRef = event.target.closest('button');

    this.tooltipFilterMenu.toggleMenu(event, event.target.closest('button'));
  }

  emitFilters(): void {
    const activeFilters = Array.from(this.filters.values());

    this.filtersEmitter.emit(activeFilters);
  }

  toggleActiveFilter(filter: Filter, event: any): void {
    filter.active = !filter.active;

    this.updateCurrentFilter(filter);

    this.tooltipFilterMenu.toggleMenu(event, this.currentFilterElementRef);

    this.emitFilters();
  }

  toggleNewFilter(event: any): void {
    this.resetCurrentFilter();

    this.mode = Mode.ADD;

    this.tooltipNewFilter.toggleMenu(event, event.target.closest('button'));
  }

  resetCurrentFilter(): void {
    this.currentFilter = null;
    this.selectedFilter = null;
  }

  toggleEditFilter(event: any): void {
    this.mode = Mode.EDIT;

    this.selectedFilter = this.currentFilter.filterDefinition.key;

    this.tooltipFilterMenu.toggleMenu(event);

    this.tooltipNewFilter.toggleMenu(event, this.currentFilterElementRef);
  }

  editFilter(event: any): void {
    if (!this.validateBeforeSave(this.currentFilter)) {
      return;
    }

    this.updateCurrentFilter(this.currentFilter);

    this.tooltipNewFilter.toggleMenu(event);

    if (this.currentFilter.active) {
      this.emitFilters();
    }

    this.resetCurrentFilter();
  }

  deleteFilter(filter: Filter, event: any): void {
    this.filters.delete(filter.filterDefinition.key);

    this.tooltipFilterMenu.toggleMenu(event);

    this.emitFilters();
  }

  addFilter(event: any): void {
    if (!this.validateBeforeSave(this.currentFilter)) {
      return;
    }

    this.updateCurrentFilter(this.currentFilter);

    this.tooltipNewFilter.toggleMenu(event);

    this.resetCurrentFilter();

    this.emitFilters();
  }

  validateBeforeSave(filter: Filter): boolean {
    return filter.filterDefinition.type == FilterType.SINGLE_INPUT ||
      filter.filterDefinition.type == FilterType.INTERVAL
      ? !!this.validateField(filter.data)
      : true;
  }

  cancelAddEditFilter(event: any): void {
    this.tooltipNewFilter.toggleMenu(event);

    this.resetCurrentFilter();
  }

  disableAllFilters(): void {
    this.filters.forEach((f) => (f.active = false));

    this.emitFilters();
  }

  enableAllFilters(): void {
    this.filters.forEach((f) => (f.active = true));

    this.emitFilters();
  }

  invertInNotIn(filter: Filter): void {
    const tmp = filter.data[LogFiltersType.NOTIN];

    filter.data[LogFiltersType.NOTIN] = filter.data[LogFiltersType.IN];

    filter.data[LogFiltersType.IN] = tmp;
  }

  invertInNotInEvent(filter: Filter, event): void {
    this.invertInNotIn(filter);

    this.updateCurrentFilter(filter);

    this.tooltipFilterMenu.toggleMenu(event);

    this.emitFilters();
  }

  formatValueToPrint(filter: Filter): string {
    return filter.filterDefinition.choices.find((choice) => choice.value == filter.data)?.slug;
  }

  handlePaste(event, filter: Filter, type: LogFiltersType): void {
    const value = event.clipboardData.getData('text/plain');

    if (value != null && value.match(/[,;]/)) {
      event.preventDefault();

      const split = value.split(/[,;]/);

      split.forEach((s) => {
        if (filter.data[type].find((d) => d.value == s)) {
          return;
        }

        if (!this.validateField(s)) {
          return;
        }

        filter.data[type] = [{ slug: s, value: s }, ...filter.data[type]];
      });

      event.target.value = '';
    }
  }

  updateCurrentFilter(filter: Filter): void {
    this.filters.set(filter.filterDefinition.key, filter);
  }

  formatValuesToPrint(data: Array<any>): string {
    return data.map((d) => d.slug).join(', ');
  }

  validateField = (data): Object => {
    switch (this.currentFilter.filterDefinition.key) {
      case FiltersEnum.IP:
        if (!isValidIpOrCidr(data)) {
          this.toastr.error('InvalidIpOrCidr');
          return null;
        }
        break;

      case FiltersEnum.RESPONSE_TIME_GTE:
        if (!isValidPositiveInt(data)) {
          this.toastr.error('WrongResponseTimeGte');
          return null;
        }
        break;

      case FiltersEnum.CONTENT_LENGTH_GTE:
        if (!isValidPositiveInt(data)) {
          this.toastr.error('WrongResponseTimeGte');
          return null;
        }
        break;

      case FiltersEnum.CREDIBILITY_BETWEEN:
        if (
          !validatePositiveInt0to100(data[LogFiltersType.GTE]) ||
          !validatePositiveInt0to100(data[LogFiltersType.LTE]) ||
          Number(data[LogFiltersType.GTE]) >= Number(data[LogFiltersType.LTE])
        ) {
          this.toastr.error('WrongCredibilityBetween');
          return null;
        }
        break;

      case FiltersEnum.STATUS_CODE:
        if (!isValidHttpCode(data)) {
          this.toastr.error('WrongHttpCode');
          return null;
        }
        break;

      case FiltersEnum.METHOD:
        if (!isValidHttpMethod(data)) {
          this.toastr.error('WrongMethodCode');
          return null;
        }
        break;
    }

    return {
      slug: data,
      value: data,
    };
  };

  private initFiltersFromInput(): void {
    if (!this.filtersInput?.length || this.filtersInput.length === 0) {
      this.filters.clear();
      return;
    }

    this.filters = new Map(
      this.filtersInput.map((filter) => [
        filter.filterDefinition.key,
        copyObject({
          ...filter,
          filterDefinition: this.allFilterDefinitions[filter.filterDefinition.key] ?? filter.filterDefinition,
        }),
      ]),
    );
  }

  async ensureChoicesLoaded(keys: FiltersEnum[]): Promise<void> {
    const loaders: Promise<void>[] = [];
    if (keys.includes(FiltersEnum.COUNTRY)) loaders.push(this.getCountries());
    if (keys.includes(FiltersEnum.ATTACK_CATEGORY)) loaders.push(this.getAttacksCategories());
    await Promise.all(loaders);
  }

  private loadChoices(key: FiltersEnum, getChoices: () => Promise<Choice[]>): Promise<void> {
    const filter = this.allFilterDefinitions[key];

    if (filter?.choices?.length) {
      return Promise.resolve();
    }

    const pending = this.pendingChoices.get(key);
    if (pending) {
      return pending;
    }

    const load = getChoices()
      .then((choices) => {
        filter.choices = choices.sort((a, b) => sortAlphabetically(a.slug, b.slug));
      })
      .finally(() => this.pendingChoices.delete(key));

    this.pendingChoices.set(key, load);
    return load;
  }
}
